import { cn } from "@/lib/utils";

export type ProgressBarProps = {
  value?: number;
  max?: number;
  indeterminate?: boolean;
  color?: "brand" | "green" | "red" | "yellow" | "blue";
  size?: "sm" | "md" | "lg";
  className?: string;
};

export type WinLossBarProps = {
  wins: number;
  losses: number;
  size?: "sm" | "md" | "lg";
  className?: string;
};

const colorClasses = {
  brand: "bg-brand-500",
  green: "bg-green-500",
  red: "bg-red-500",
  yellow: "bg-yellow-500",
  blue: "bg-blue-500",
};

const sizeClasses = {
  sm: "h-1",
  md: "h-1.5",
  lg: "h-2",
};

export function ProgressBar({
  value = 0,
  max = 100,
  indeterminate = false,
  color = "brand",
  size = "md",
  className = "",
}: ProgressBarProps) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div
      className={cn(
        "w-full bg-surface-700 rounded-full overflow-hidden",
        sizeClasses[size],
        className,
      )}
    >
      {indeterminate ? (
        <div
          className={cn(
            "h-full w-1/3 rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]",
            colorClasses[color],
          )}
        />
      ) : (
        <div
          className={cn("h-full rounded-full transition-all", colorClasses[color])}
          style={{ width: `${percentage}%` }}
        />
      )}
    </div>
  );
}

export function WinLossBar({ wins, losses, size = "md", className = "" }: WinLossBarProps) {
  const total = wins + losses;
  const winPercentage = total > 0 ? (wins / total) * 100 : 50;

  return (
    <div className={cn("w-full flex rounded-full overflow-hidden", sizeClasses[size], className)}>
      <div className="h-full bg-brand-500 transition-all" style={{ width: `${winPercentage}%` }} />
      <div
        className="h-full bg-red-500 transition-all"
        style={{ width: `${100 - winPercentage}%` }}
      />
    </div>
  );
}
